/**
 * Sentry-powered ErrorBoundary wrapper
 *
 * Delegates all error capture, component stack extraction, and optional
 * crash-report dialog to the `@sentry/react` ErrorBoundary. Preserves
 * the existing fallback UI for visual consistency.
 */

import * as Sentry from "@sentry/react";
import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FlexLayout, StackLayout } from "@/design-system/salt";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Show the Sentry crash-report dialog on error (default: false) */
  showDialog?: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("Failed to fetch dynamically imported module")
  );
}

/** Default fallback rendered when no custom fallback is provided */
function DefaultFallback({
  error,
  resetError,
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) {
  // Auto-reload on chunk load errors (stale deploy / stale cache) — transparent to the user.
  // A sessionStorage flag prevents infinite reload loops.
  //
  // The reload must be cache-busting: a stale cached index.html references
  // chunk hashes that no longer exist on the server, and Vercel's SPA
  // fallback returns text/html for missing .js files (MIME mismatch).  A
  // plain window.location.reload() may re-serve the stale index.html from
  // the HTTP cache, perpetuating the error.  Appending a timestamp forces
  // the browser to re-fetch index.html and pick up the current chunk hashes.
  if (isChunkLoadError(error)) {
    const alreadyRetried = sessionStorage.getItem("chunk_reload_attempted");
    if (!alreadyRetried) {
      sessionStorage.setItem("chunk_reload_attempted", "1");
      const url = new URL(window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      window.location.replace(url.toString());
      return null;
    }
    sessionStorage.removeItem("chunk_reload_attempted");
  }

  return (
    <FlexLayout
      align="center"
      justify="center"
      style={{
        minHeight: "100vh",
        background: "var(--salt-container-secondary-background)",
        padding: "var(--salt-spacing-300)",
      }}
    >
      <StackLayout gap={2} direction="column" style={{ width: "100%", maxWidth: "28rem" }}>
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>
            An unexpected error occurred. You can try again or reload the page.
          </AlertDescription>
        </Alert>
        {import.meta.env.DEV && error instanceof Error && (
          <pre style={{ fontFamily: "var(--salt-text-code-fontFamily, monospace)" }}>
            {error.message}
          </pre>
        )}
        <FlexLayout wrap justify="end" gap={1}>
          <Button type="button" variant="outline" onClick={resetError}>
            Try Again
          </Button>
          <Button type="button" variant="primary" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </FlexLayout>
      </StackLayout>
    </FlexLayout>
  );
}

/** Error Boundary component. */
export function ErrorBoundary({ children, fallback, showDialog = false }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      showDialog={showDialog}
      fallback={
        fallback ? () => <>{fallback}</> : (errorData) => <DefaultFallback {...errorData} />
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
